// Reine clientseitige Rechenlogik für RegalAbstand.de (PRD §10/§20).
// Keine React-Abhängigkeit, damit die Logik testbar bleibt (siehe scripts/calc.test.js).
// Intern wird in Millimetern gerechnet, die Oberfläche arbeitet in Zentimetern.

export function round1(n) {
  if (!isFinite(n)) return n;
  return Math.round(n * 10) / 10;
}

// mm auf eine Schrittweite runden (1 / 5 / 10 mm).
export function roundToMm(mm, stepMm) {
  if (!stepMm || stepMm <= 1) return Math.round(mm);
  return Math.round(mm / stepMm) * stepMm;
}

// Millimeter als deutsches cm-Maß formatieren, z. B. "34,56 cm".
export function fmtCm(mm) {
  if (!isFinite(mm)) return "—";
  const cm = mm / 10;
  const rounded = Math.round(cm * 100) / 100;
  const s = (Number.isInteger(rounded)
    ? rounded.toString()
    : rounded.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")
  ).replace(".", ",");
  return `${s} cm`;
}

function toNum(v) {
  if (v === "" || v === null || v === undefined) return NaN;
  return typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
}

// ----------------------------------------------------------------------------
// Regalböden / Fächer verteilen (PRD §10, §20, §21)
// ----------------------------------------------------------------------------
// Eingabe (alle Längen in mm):
//   innerHeightMm    lichte Innenhöhe
//   mode             "compartments" | "shelves" | "one_fixed_compartment"
//   compartmentCount Anzahl Fächer (mode compartments / one_fixed_compartment)
//   shelfCount       Anzahl Einlegeböden (mode shelves)
//   shelfThicknessMm Bodenstärke
//   roundingMm       1 | 5 | 10 (optional)
//   fixedCompartment { index, heightMm } (mode one_fixed_compartment)
//   markingReference "shelf_bottom" | "shelf_top" | "hole_center" (Default shelf_bottom)
//   holeOffsetMm     Bohrlochabstand zur Bodenunterkante (nur hole_center)
//
// Wichtigste Regel: das Tool unterscheidet eindeutig zwischen FÄCHERN und BÖDEN.
export function calcShelves(input) {
  const innerHeightMm = toNum(input.innerHeightMm);
  const shelfThicknessMm = toNum(input.shelfThicknessMm);
  const mode = input.mode || "compartments";
  const roundingMm = input.roundingMm ? Number(input.roundingMm) : null;
  const markingReference = input.markingReference || "shelf_bottom";
  const holeOffsetMm = input.holeOffsetMm !== undefined ? toNum(input.holeOffsetMm) : 0;

  const errors = [];
  const warnings = [];

  // Anzahl Fächer / Böden je nach Modus ableiten.
  let compartmentCount;
  let shelfCount;
  if (mode === "shelves") {
    shelfCount = Math.round(toNum(input.shelfCount));
    compartmentCount = shelfCount + 1;
  } else {
    compartmentCount = Math.round(toNum(input.compartmentCount));
    shelfCount = compartmentCount - 1;
  }

  // Eingabe-Validierung.
  if (!isFinite(innerHeightMm) || innerHeightMm <= 0) errors.push("Bitte eine gültige Innenhöhe eingeben.");
  if (!isFinite(shelfThicknessMm) || shelfThicknessMm < 0) errors.push("Bitte eine gültige Bodenstärke eingeben.");
  if (!isFinite(compartmentCount) || compartmentCount < 1) errors.push("Bitte eine gültige Anzahl eingeben.");

  const bail = () => ({ ok: false, errors, warnings, mode, innerHeightMm, shelfCount, compartmentCount, shelfThicknessMm });
  if (errors.length) return bail();

  const totalThicknessMm = shelfCount * shelfThicknessMm;
  if (totalThicknessMm >= innerHeightMm) {
    errors.push("Die Bodenstärken sind zusammen höher als die Innenhöhe.");
    return bail();
  }

  const availableHeightMm = innerHeightMm - totalThicknessMm;

  // Fachhöhen bestimmen (gleichmäßig, ggf. ein festes Fach).
  let compartmentHeightsMm;
  if (mode === "one_fixed_compartment" && input.fixedCompartment) {
    const idx = Math.round(toNum(input.fixedCompartment.index));
    const fixedHeight = toNum(input.fixedCompartment.heightMm);
    if (!isFinite(idx) || idx < 1 || idx > compartmentCount) {
      errors.push("Das feste Fach liegt außerhalb des gültigen Bereichs.");
      return bail();
    }
    if (!isFinite(fixedHeight) || fixedHeight <= 0 || fixedHeight >= availableHeightMm) {
      errors.push("Die Höhe des festen Fachs ist nicht möglich.");
      return bail();
    }
    const remaining = availableHeightMm - fixedHeight;
    const regular = remaining / (compartmentCount - 1);
    compartmentHeightsMm = Array.from({ length: compartmentCount }, (_, i) => (i + 1 === idx ? fixedHeight : regular));
  } else {
    const h = availableHeightMm / compartmentCount;
    compartmentHeightsMm = Array.from({ length: compartmentCount }, () => h);
  }

  // Warnungen.
  const minHeight = Math.min(...compartmentHeightsMm);
  if (minHeight < 100) warnings.push("Die Fächer werden sehr niedrig. Prüfe, ob die Anzahl der Böden sinnvoll ist.");
  if (roundingMm) warnings.push("Durch Rundung entstehen kleine Abweichungen. Miss konsequent nach derselben Markierliste.");

  // Markierpositionen je Boden ab Innen-Unterkante.
  // Unterkante Boden i = Summe der Fächer darunter + Stärke der Böden darunter.
  const shelfPositions = [];
  let cumCompartments = 0;
  for (let i = 1; i <= shelfCount; i++) {
    cumCompartments += compartmentHeightsMm[i - 1];
    const bottomBase = cumCompartments + (i - 1) * shelfThicknessMm;
    let bottomPositionMm = bottomBase;
    if (markingReference === "shelf_top") bottomPositionMm = bottomBase + shelfThicknessMm;
    else if (markingReference === "hole_center") bottomPositionMm = bottomBase + holeOffsetMm;
    const topPositionMm = bottomBase + shelfThicknessMm;
    const pos = {
      shelfIndex: i,
      bottomPositionMm: round1(bottomPositionMm),
      topPositionMm: round1(topPositionMm),
    };
    if (roundingMm) {
      pos.roundedBottomPositionMm = roundToMm(bottomPositionMm, roundingMm);
      pos.roundedTopPositionMm = roundToMm(topPositionMm, roundingMm);
    }
    shelfPositions.push(pos);
  }

  return {
    ok: true,
    errors,
    warnings,
    mode,
    markingReference,
    innerHeightMm,
    shelfCount,
    compartmentCount,
    shelfThicknessMm,
    totalThicknessMm: round1(totalThicknessMm),
    availableHeightMm: round1(availableHeightMm),
    compartmentHeightsMm: compartmentHeightsMm.map(round1),
    compartmentHeightMm: round1(compartmentHeightsMm[0]),
    shelfPositions,
  };
}
